import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import {
    Card,
    CardBody,
    VStack,
    Text,
    Button,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Input,
    InputGroup,
    InputRightElement,
    IconButton,
    Alert,
    AlertIcon,
    AlertDescription,
    useToast,
    useColorMode,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { getColor } from "../../../brandConfig";
import { useAuth } from "../../../contexts/AuthContext";
import type { Mutation, MutationChangeMyPasswordArgs } from "../../../generated/graphql";
import { CHANGE_MY_PASSWORD, SET_PASSWORD } from "../queries";
import { validatePassword, PASSWORD_MIN_LENGTH } from "../utils";
import { errorWords, signedOutAtWords } from "../utils/assurance";
// Every tooltip goes through the kit's SafeTooltip — it can never stay on screen (Polish rule 11).
import { SafeTooltip as Tooltip } from "./ui/SafeTooltip";

export const ChangePasswordCard: React.FC = () => {
    const toast = useToast();
    const { colorMode } = useColorMode();
    const auth = useAuth();
    const { user, refreshAuth } = auth;

    const cardGradientBg = getColor("background.cardGradient", colorMode);
    const cardBorder = getColor("border.darkCard", colorMode);
    const textPrimary = getColor(colorMode === "light" ? "text.primary" : "text.primaryDark", colorMode);
    const textMuted = getColor(colorMode === "light" ? "text.muted" : "text.mutedDark", colorMode);
    const primaryColor = getColor("primary.500", colorMode);
    const primaryHover = getColor("primary.600", colorMode);
    const inputBg = "rgba(255, 255, 255, 0.05)";

    const hasPassword = !!user?.passwordChangedAt;

    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    // changeMyPassword (2026-09-28): changes the password AND signs out every other device,
    // returning a fresh token for this one (adopted below — the old token stops working).
    const [changePasswordMutation, { loading: changing }] = useMutation<Pick<Mutation, "changeMyPassword">, MutationChangeMyPasswordArgs>(CHANGE_MY_PASSWORD);
    const [setPasswordMutation, { loading: setting }] = useMutation(SET_PASSWORD);
    const loading = changing || setting;

    const passwordError = newPassword.length > 0 ? validatePassword(newPassword) : null;
    const confirmMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
    const currentMissing = hasPassword && currentPassword.length === 0;
    const canSubmit =
        !loading &&
        !passwordError &&
        !confirmMismatch &&
        newPassword.length >= PASSWORD_MIN_LENGTH &&
        confirmPassword.length > 0 &&
        !currentMissing;

    const clearFields = () => {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
    };

    const handleSubmit = async () => {
        if (!canSubmit) return;

        try {
            let signedOutAt: string | null = null;
            if (hasPassword) {
                const res = await changePasswordMutation({
                    variables: { input: { currentPassword, newPassword } },
                });
                const out = res.data?.changeMyPassword;
                if (!out?.token) throw new Error("The server didn't confirm the change. Your password is unchanged — please try again.");
                if (auth.adoptToken) await auth.adoptToken(out.token);
                else { localStorage.setItem("auth_token", out.token); await refreshAuth?.(); }
                signedOutAt = out.signedOutAt;
            } else {
                await setPasswordMutation({
                    variables: { input: { password: newPassword } },
                });
            }

            toast({
                title: hasPassword ? "Password updated" : "Password set",
                description: hasPassword
                    ? `Your password has been changed. Every other device was signed out at ${signedOutAtWords(signedOutAt)}; this one is still signed in.`
                    : "You can now sign in with your username and password.",
                status: "success",
                duration: hasPassword ? 7000 : 4000,
                isClosable: true,
            });
            clearFields();
            if (!hasPassword) refreshAuth();
        } catch (err: any) {
            toast({
                title: "Couldn't update password",
                description: errorWords(err, "Please try again."),
                status: "error",
                duration: 6000,
                isClosable: true,
            });
        }
    };

    return (
        <Card bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px">
            <CardBody>
                <VStack spacing={4} align="stretch">
                    <VStack align="start" spacing={1}>
                        <Text color={textPrimary} fontWeight="bold" fontSize="lg">
                            {hasPassword ? "Change Password" : "Set Password"}
                        </Text>
                        <Text color={textMuted} fontSize="md">
                            Choose a password at least {PASSWORD_MIN_LENGTH} characters long. You'll use it with
                            {user?.username ? ` your username "${user.username}"` : " your username"} to log in.
                        </Text>
                        {hasPassword && (
                            <Text color={textPrimary} fontSize="md" data-testid="change-password-signs-out">
                                Changing your password signs you out on every other phone, tablet and computer. This device stays signed in.
                            </Text>
                        )}
                    </VStack>

                    {!hasPassword && (
                        <Alert status="info" borderRadius="md" variant="left-accent">
                            <AlertIcon />
                            <AlertDescription fontSize="sm">
                                You don't have a password yet. Set one now so you can log in directly without an SMS or email code.
                            </AlertDescription>
                        </Alert>
                    )}

                    {hasPassword && (
                        <FormControl>
                            <FormLabel color={textPrimary}>Current Password</FormLabel>
                            <InputGroup>
                                <Input
                                    bg={inputBg}
                                    borderColor={cardBorder}
                                    color={textPrimary}
                                    type={showCurrent ? "text" : "password"}
                                    autoComplete="current-password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                />
                                <InputRightElement>
                                    <Tooltip label={showCurrent ? "Hide password" : "Show password"} openDelay={300} hasArrow>
                                        <IconButton
                                            aria-label={showCurrent ? "Hide current password" : "Show current password"}
                                            icon={showCurrent ? <ViewOffIcon /> : <ViewIcon />}
                                            size="sm"
                                            variant="ghost"
                                            color={textMuted}
                                            onClick={() => setShowCurrent(!showCurrent)}
                                        />
                                    </Tooltip>
                                </InputRightElement>
                            </InputGroup>
                        </FormControl>
                    )}

                    <FormControl isInvalid={!!passwordError}>
                        <FormLabel color={textPrimary}>New Password</FormLabel>
                        <InputGroup>
                            <Input
                                bg={inputBg}
                                borderColor={cardBorder}
                                color={textPrimary}
                                type={showNew ? "text" : "password"}
                                autoComplete="new-password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                            />
                            <InputRightElement>
                                <Tooltip label={showNew ? "Hide password" : "Show password"} openDelay={300} hasArrow>
                                    <IconButton
                                        aria-label={showNew ? "Hide new password" : "Show new password"}
                                        icon={showNew ? <ViewOffIcon /> : <ViewIcon />}
                                        size="sm"
                                        variant="ghost"
                                        color={textMuted}
                                        onClick={() => setShowNew(!showNew)}
                                    />
                                </Tooltip>
                            </InputRightElement>
                        </InputGroup>
                        {passwordError && <FormErrorMessage>{passwordError}</FormErrorMessage>}
                    </FormControl>

                    <FormControl isInvalid={confirmMismatch}>
                        <FormLabel color={textPrimary}>Confirm New Password</FormLabel>
                        <InputGroup>
                            <Input
                                bg={inputBg}
                                borderColor={cardBorder}
                                color={textPrimary}
                                type={showConfirm ? "text" : "password"}
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && canSubmit) handleSubmit();
                                }}
                            />
                            <InputRightElement>
                                <Tooltip label={showConfirm ? "Hide password" : "Show password"} openDelay={300} hasArrow>
                                    <IconButton
                                        aria-label={showConfirm ? "Hide confirm password" : "Show confirm password"}
                                        icon={showConfirm ? <ViewOffIcon /> : <ViewIcon />}
                                        size="sm"
                                        variant="ghost"
                                        color={textMuted}
                                        onClick={() => setShowConfirm(!showConfirm)}
                                    />
                                </Tooltip>
                            </InputRightElement>
                        </InputGroup>
                        {confirmMismatch && <FormErrorMessage>Passwords do not match</FormErrorMessage>}
                    </FormControl>

                    <Tooltip
                        label={
                            !canSubmit && !loading
                                ? "Fill in all fields with a matching password of at least 8 characters"
                                : hasPassword
                                    ? "Save your new password.\n\nThis changes your account: every other device signed in to it is signed out straight away. This device stays signed in."
                                    : "Set your password. This changes your account: you can then sign in with your username and password."
                        }
                        openDelay={300}
                        hasArrow
                        whiteSpace="pre-line"
                    >
                        <Button
                            size="lg"
                            minH="48px"
                            bg={primaryColor}
                            color="white"
                            _hover={{ bg: primaryHover }}
                            onClick={handleSubmit}
                            isLoading={loading}
                            loadingText={hasPassword ? "Updating..." : "Setting..."}
                            isDisabled={!canSubmit}
                            alignSelf="flex-end"
                        >
                            {hasPassword ? "Update Password" : "Set Password"}
                        </Button>
                    </Tooltip>
                </VStack>
            </CardBody>
        </Card>
    );
};
